import { useState, useEffect, useRef } from 'react'
import { Navbar } from './components/Navbar'
import { Hero } from './components/Hero'
import { Expertise } from './components/Expertise'
import { Process } from './components/Process'
import { About } from './components/About'
import { Insights } from './components/Insights'
import { Contact } from './components/Contact'
import { Footer } from './components/Footer'
import { ThankYou } from './components/ThankYou'

type PageView = 'home' | 'thank-you'

function App() {
  const [currentPage, setCurrentPage] = useState<PageView>('home')
  const mainRef = useRef<HTMLElement>(null)

  // Handle hash-based routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1)
      if (hash === 'thank-you') {
        setCurrentPage('thank-you')
      } else {
        setCurrentPage('home')
      }
    }

    handleHashChange()
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // Announce page changes to screen readers
  useEffect(() => {
    const announcer = document.getElementById('route-announcer')
    if (announcer) {
      const pageName = currentPage === 'home' ? 'Home' : 'Thank You'
      announcer.textContent = `Navigated to ${pageName} page`
    }

    // Scroll to top on page change
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentPage])

  const handleNavigate = (page: PageView | string) => {
    if (page === 'home') {
      window.location.hash = ''
      setCurrentPage('home')
    } else if (page === 'thank-you') {
      window.location.hash = 'thank-you'
      setCurrentPage('thank-you')
    } else if (page.startsWith('#')) {
      // Smooth scroll to section
      const element = document.querySelector(page)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar onNavigate={handleNavigate} />

      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        className="flex-1 outline-none"
        role="main"
      >
        {currentPage === 'home' ? (
          <>
            <Hero onNavigate={handleNavigate} />
            <Expertise />
            <Process />
            <About />
            <Insights />
            <Contact onNavigate={handleNavigate} />
          </>
        ) : (
          <ThankYou onNavigate={handleNavigate} />
        )}
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  )
}

export default App
